import { useState } from 'react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTracking, track } from 'lib/analytics'
import { updateProductFilters } from 'store/components/product-filters/actions'
import { InfoButton, CloseButton } from 'components/buttons'
import { omit } from 'lodash'
import {
  SortSection,
  BrandsFilterSection,
  ProductTypeFilterSection,
  PriceFilterSection,
  RatingFilterSection,
  InStockFilterSection,
  OnSaleFilterSection,
} from './sections'

import {
  SECTIONS,
  getPriceRange,
  getBrandOptions,
  hideProductType,
  getCategoryOptions,
  clearedFilterOptions,
  getFilteredProductsCount,
} from '../helpers'

const ButtonToolbar = ({ clearAllFilters, applyFilters, productCount }) => (
  <div
    className="flex inline-block justify-center pal white-bg"
    style={{
      left: '0px',
      bottom: '0px',
      width: '100%',
      position: 'absolute',
      boxShadow: '#f2f2f2 0px -15px 20px 0px',
    }}
  >
    <InfoButton
      pill
      empty
      className="mrm"
      onClick={clearAllFilters}
      style={{ minWidth: 116 }}
    >
      Clear All
    </InfoButton>
    <InfoButton pill block onClick={applyFilters} disabled={productCount === 0}>
      {`View ${productCount} ${pluralize('Item', productCount)}`}
    </InfoButton>
  </div>
)

ButtonToolbar.propTypes = {
  applyFilters: PropTypes.func.isRequired,
  productCount: PropTypes.number.isRequired,
  clearAllFilters: PropTypes.func.isRequired,
}

export const FiltersPanelBase = ({
  defaults,
  closePanel,
  updateFilters,
  initialProducts,
  initialOpenSection,
}) => {
  const tracker = useTracking()
  const [minRating, setMinRating] = useState(defaults.minRating)
  const [minPrice, setMinPrice] = useState(defaults.minPrice)
  const [maxPrice, setMaxPrice] = useState(defaults.maxPrice)
  const [onSale, setOnSale] = useState(defaults.onSale)
  const [categories, setCategories] = useState(defaults.categories || [])
  const [brands, setBrands] = useState(defaults.brands || [])
  const [available, setAvailable] = useState(defaults.available)

  const brandOptions = getBrandOptions(initialProducts)
  const categoryOptions = getCategoryOptions(initialProducts)
  const priceRange = getPriceRange(initialProducts, minPrice, maxPrice)

  const filters = {
    minRating,
    minPrice,
    maxPrice,
    onSale,
    categories,
    brands,
    available,
  }

  const productCount = getFilteredProductsCount(initialProducts, filters)

  const applyFilters = () => {
    closePanel()
    updateFilters(filters)

    tracker.trackEvent({
      event: track.filtersApplied,
      filters: JSON.stringify(filters),
    })
  }

  const clearAllFilters = () => {
    closePanel()
    updateFilters(clearedFilterOptions)
  }

  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      <CloseButton
        onClick={closePanel}
        className="mtl mll"
        style={{ float: 'left' }}
      />
      <div
        className="inline-block mbxl"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'scroll',
          paddingBottom: '150px',
        }}
      >
        <hr className="mvl" />
        <SortSection open={initialOpenSection === SECTIONS.SORT} />
        <hr className="mvl" />
        <BrandsFilterSection
          brands={brands}
          setBrands={setBrands}
          products={initialProducts}
          options={brandOptions}
          open={initialOpenSection === SECTIONS.BRANDS}
          selfExcludingFilters={omit(filters, 'brands')}
        />
        {!hideProductType() && (
          <>
            <hr className="mvl" />
            <ProductTypeFilterSection
              categories={categories}
              products={initialProducts}
              setCategories={setCategories}
              options={categoryOptions}
              open={initialOpenSection === SECTIONS.PRODUCT_TYPE}
              selfExcludingFilters={omit(filters, 'categories')}
            />
          </>
        )}
        <hr className="mvl" />
        <RatingFilterSection
          products={initialProducts}
          minRating={minRating}
          setMinRating={setMinRating}
          open={initialOpenSection === SECTIONS.RATING}
          selfExcludingFilters={omit(filters, 'minRating')}
        />
        <hr className="mvl" />
        <PriceFilterSection
          minPrice={minPrice}
          maxPrice={maxPrice}
          setMinPrice={setMinPrice}
          setMaxPrice={setMaxPrice}
          productCount={productCount}
          priceRange={priceRange}
          open={initialOpenSection === SECTIONS.PRICE_RANGE}
          selfExcludingFilters={omit(filters, ['minPrice', 'maxPrice'])}
        />
        <hr className="mvl" />
        <InStockFilterSection
          products={initialProducts}
          inStock={available}
          setInStock={setAvailable}
          selfExcludingFilters={omit(filters, 'available')}
        />
        <hr className="mvl" />
        <OnSaleFilterSection
          products={initialProducts}
          onSale={onSale}
          setOnSale={setOnSale}
          selfExcludingFilters={omit(filters, 'onSale')}
        />
        <hr className="mvl" />
      </div>
      <ButtonToolbar
        applyFilters={applyFilters}
        productCount={productCount}
        clearAllFilters={clearAllFilters}
      />
    </div>
  )
}

FiltersPanelBase.propTypes = {
  defaults: PropTypes.shape({
    minRating: PropTypes.number,
    minPrice: PropTypes.number,
    maxPrice: PropTypes.number,
    onSale: PropTypes.bool,
    categories: PropTypes.arrayOf(PropTypes.string),
    brands: PropTypes.arrayOf(PropTypes.string),
    available: PropTypes.bool,
  }).isRequired,
  closePanel: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  /* eslint-disable-next-line react/forbid-prop-types */
  initialProducts: PropTypes.array.isRequired,
  initialOpenSection: PropTypes.string.isRequired,
}

const mapDispatchToProps = (dispatch) => ({
  updateFilters: (newFilters) => dispatch(updateProductFilters(newFilters)),
})

const mapStateToProps = (state) => ({
  defaults: state.productFilters,
})

export const FiltersPanel = connect(
  mapStateToProps,
  mapDispatchToProps
)(FiltersPanelBase)
